/**
 * Custom JavaScript for Hirsch Hill theme.
 *
 * Fixes full-page reload when switching between linked-product variants
 * (variants that are separate Shopify products connected via the
 * linked-products / product-swatches feature).
 *
 * WHY XHR INSTEAD OF fetch():
 *   The Wholesale Gorilla app (wsg-discounts.js) monkey-patches window.fetch
 *   globally. Depending on its implementation it may consume or wrap the
 *   response body in a way that prevents our subsequent .json() call.
 *   XMLHttpRequest is much less commonly patched and gives us a clean path
 *   to the response text.
 */

(function () {
  'use strict';

  var LOG = '[HH-swap]';

  // ─── Patch window.fetch for variant rendering calls ──────────────────────
  //
  // theme.js renderForCombination_fn (line 7231) calls window.fetch to load
  // the section HTML for a selected option combination:
  //   fetch(`/products/handle?option_values=...&section_id=...`).text()
  //
  // Wholesale Gorilla monkey-patches window.fetch globally.  If its wrapper
  // reads or wraps the Response body before returning it, the subsequent
  // .text() call fails ("body already used"), selectCombination throws,
  // <select name="id"> is never updated, and the default variant is added
  // to the cart instead of the selected one.
  //
  // Fix: wrap window.fetch AFTER WSG so that requests containing
  // "option_values=" are handled by raw XHR (bypassing WSG entirely).
  // All other URLs continue through WSG's patched fetch unchanged.

  (function () {
    var _wsgFetch = window.fetch;
    window.fetch = function (resource, init) {
      var url = typeof resource === 'string' ? resource
              : (resource && resource.url ? resource.url : String(resource));

      if (url.indexOf('option_values=') !== -1) {
        console.log(LOG, 'fetch intercepted for option_values — using XHR bypass:', url.split('?')[1]);
        return new Promise(function (resolve, reject) {
          var xhr = new XMLHttpRequest();
          xhr.open('GET', url, true);
          xhr.setRequestHeader('Accept', 'text/html');
          xhr.onload = function () {
            var body = xhr.responseText;
            // Return a minimal fetch-Response-compatible object
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              headers: { get: function () { return null; } },
              text: function () { return Promise.resolve(body); },
              json: function () { return Promise.resolve(JSON.parse(body)); },
              clone: function () { return this; },
            });
          };
          xhr.onerror = function () { reject(new Error('XHR network error for ' + url)); };
          xhr.send();
        });
      }

      // All other URLs go through WSG's patched fetch as normal
      return _wsgFetch.apply(this, arguments);
    };
  })();

  // ─── Shared AJAX swap logic ───────────────────────────────────────────────

  var _swapInProgress = false;

  function doAjaxProductSwap(targetHref) {
    if (_swapInProgress) {
      console.log(LOG, 'swap already in progress – ignoring', targetHref);
      return;
    }
    _swapInProgress = true;
    console.log(LOG, 'starting swap to', targetHref);
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    // ── Use XHR to fetch the full product page.
    // We use XHR rather than fetch() because WSG monkey-patches window.fetch
    // and may consume or corrupt the response body before we can read it.
    // DOMParser.parseFromString() never executes scripts, so fetching the
    // full page HTML is safe — we strip every <script> before inserting
    // the new section into the live DOM.
    var xhr = new XMLHttpRequest();
    // Append ?_hh=1 so we can identify our own requests in the network tab
    xhr.open('GET', targetHref + (targetHref.indexOf('?') >= 0 ? '&' : '?') + '_hh=1', true);
    xhr.setRequestHeader('Accept', 'text/html');

    xhr.onload = function () {
      console.log(LOG, 'XHR complete, status:', xhr.status, 'length:', xhr.responseText.length);

      if (xhr.status < 200 || xhr.status >= 300) {
        console.warn(LOG, 'non-2xx response, falling back to navigation');
        _swapInProgress = false;
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
        window.location.href = targetHref;
        return;
      }

      try {
        var parser = new DOMParser();
        var doc = parser.parseFromString(xhr.responseText, 'text/html');

        // Find the product section wrapper in the fetched page
        var newSection = doc.getElementById('shopify-section-main');
        if (!newSection) {
          // Fallback: find by data-section-type attribute
          var typedSection = doc.querySelector('[data-section-type="product"]');
          if (typedSection) newSection = typedSection.closest('[id^="shopify-section-"]') || typedSection;
        }
        console.log(LOG, 'newSection found:', !!newSection, newSection && newSection.id);

        var currentSection = document.getElementById('shopify-section-main');
        console.log(LOG, 'currentSection found:', !!currentSection, currentSection && currentSection.id);

        if (!currentSection || !newSection) {
          throw new Error('section elements not found (current=' + !!currentSection + ', new=' + !!newSection + ')');
        }

        // ── Collect the DOB checker script BEFORE removing all scripts.
        // We identify it by its unique localStorage key.
        var dobScript = null;
        newSection.querySelectorAll('script:not([src])').forEach(function (s) {
          if (s.textContent.indexOf('hh_dob_verified') !== -1) {
            dobScript = s.textContent;
            console.log(LOG, 'DOB checker script found, length:', dobScript.length);
          }
        });

        // ── Strip ALL scripts from the new section before inserting.
        // This prevents Wholesale Gorilla and other third-party app inline
        // scripts from re-running and causing a redirect on mobile.
        var removed = 0;
        newSection.querySelectorAll('script').forEach(function (s) {
          s.parentNode.removeChild(s);
          removed++;
        });
        console.log(LOG, 'removed', removed, 'script tags from new section');

        // ── Swap into the live DOM
        currentSection.replaceWith(newSection);
        console.log(LOG, 'section swapped');

        // ── Re-run only the DOB checker so the age gate is active on the
        // new product's buy buttons.
        if (dobScript) {
          var exec = document.createElement('script');
          exec.textContent = dobScript;
          document.head.appendChild(exec);
          exec.parentNode.removeChild(exec);
          console.log(LOG, 'DOB checker re-executed');
        }

        // ── Update URL in browser history
        history.pushState({ path: targetHref }, '', targetHref);
        console.log(LOG, 'pushState done, URL now:', window.location.href);

        // ── Re-init Shopify accelerated checkout buttons
        try {
          if (Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
        } catch (e) {}

      } catch (err) {
        console.error(LOG, 'swap failed, falling back to navigation:', err);
        window.location.href = targetHref;
      }

      _swapInProgress = false;
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
    };

    xhr.onerror = function () {
      console.error(LOG, 'XHR network error, falling back');
      _swapInProgress = false;
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      window.location.href = targetHref;
    };

    xhr.send();
  }

  // ─── Fix 1: <select> dropdown with data-update-url ───────────────────────

  document.addEventListener(
    'change',
    function (e) {
      var select = e.target;
      if (
        !select ||
        select.tagName !== 'SELECT' ||
        !select.hasAttribute('data-option-position')
      ) return;

      var selectedOption = select.selectedOptions && select.selectedOptions[0];
      if (!selectedOption || !selectedOption.hasAttribute('data-update-url')) return;

      var updateUrl = selectedOption.getAttribute('data-update-url');
      var targetUrl;
      try { targetUrl = new URL(updateUrl, window.location.origin); }
      catch (err) { return; }

      if (targetUrl.pathname === window.location.pathname) {
        // Same product — strip attr so theme's selectCombination() handles it
        selectedOption.removeAttribute('data-update-url');
        setTimeout(function () {
          selectedOption.setAttribute('data-update-url', updateUrl);
        }, 0);
      } else {
        // Linked product — prevent theme handler, do our own AJAX swap
        e.stopImmediatePropagation();
        doAjaxProductSwap(targetUrl.href);
      }
    },
    true
  );

  // ─── Fix 2: anchor swatch / block links for linked products ──────────────

  var SWATCH_LINK_SELECTOR = [
    'a.block-swatch__item',
    'a.color-swatch__item',
    'a.variant-swatch__item',
  ].join(', ');

  function handleSwatchEvent(e) {
    var link = e.target.closest(SWATCH_LINK_SELECTOR);
    if (!link) return;

    var href = link.getAttribute('href');
    if (!href) return;

    var targetUrl;
    try { targetUrl = new URL(href, window.location.origin); }
    catch (err) { return; }

    // Only intercept same-origin /products/ links to a DIFFERENT product
    if (targetUrl.origin !== window.location.origin) return;
    if (!targetUrl.pathname.startsWith('/products/')) return;
    if (targetUrl.pathname === window.location.pathname) return;

    console.log(LOG, e.type, 'intercepted swatch link to', targetUrl.href);
    e.preventDefault();
    link.classList.add('is-loading');
    doAjaxProductSwap(targetUrl.href);
  }

  document.addEventListener('touchend', handleSwatchEvent, true);
  document.addEventListener('click',    handleSwatchEvent, true);

  // ─── Fix 3: guarantee correct variant id on cart submission ─────────────
  //
  // BuyButton.addToCart_fn (theme.js:10970) calls event.stopPropagation() and
  // event.preventDefault() on the click, then calls Form.serialize(form)
  // directly — a 'submit' event is never fired, so a submit listener is useless.
  //
  // Easify's easifyShowOptionSetOnCustomElement() (theme.js:10906) runs during
  // product:rerender (which fires synchronously AFTER variant:changed).  It
  // can overwrite <input name="id"> with the default variant's id.
  //
  // Fix:
  //   1. Store the correct variant id when 'variant:changed' fires (before
  //      product:rerender / Easify runs).
  //   2. Intercept 'click' in capture phase — fires before BuyButton's
  //      element-level bubble handler — and correct <input name="id"> so
  //      Form.serialize() reads the right value.

  var _lastVariantId = null;

  document.addEventListener('variant:changed', function (e) {
    var variant = e.detail && e.detail.variant;
    // Always log so we can confirm this fires after the fetch fix
    console.log(LOG, 'variant:changed fired, id:', variant && variant.id);
    if (!variant || !variant.id) return;
    _lastVariantId = String(variant.id);
  }, true);

  document.addEventListener('click', function (e) {
    // Only act when something inside a <buy-button> is clicked
    var buyBtn = e.target.closest ? e.target.closest('buy-button') : null;
    if (!buyBtn) return;

    // Find the cart form this button belongs to
    var form = buyBtn.closest('form[action*="/cart/add"]');
    if (!form) return;

    var idSelect = form.querySelector('select[name="id"]');
    var idInput  = form.querySelector('input[name="id"]');
    var currentId = idSelect ? idSelect.value : (idInput ? idInput.value : '');

    console.log(LOG, 'buy-button click — select[name=id]:', currentId, '| _lastVariantId:', _lastVariantId);

    if (!_lastVariantId) return;

    // Setting select.value to an option that doesn't exist in the <select> is
    // a silent no-op — the value becomes "".  Products with >250 variants don't
    // have every variant option in the initial <select name="id"> HTML, so we
    // CANNOT rely on the select.  Instead, inject (or update) a hidden input
    // appended to the form.  Form.serialize iterates form.elements in DOM
    // order, so the last element named "id" wins — the injected input overrides
    // the select's empty value with the correct variant id.
    var override = form.querySelector('input[type="hidden"][name="id"][data-hh]');
    if (!override) {
      override = document.createElement('input');
      override.type = 'hidden';
      override.name = 'id';
      override.setAttribute('data-hh', '1');
      form.appendChild(override);
    }

    if (override.value !== _lastVariantId) {
      console.log(LOG, 'injecting variant id override:', override.value || '(empty)', '→', _lastVariantId);
      override.value = _lastVariantId;
    }
  }, true); // capture — fires before BuyButton's element-level bubble handler

  // ─── Fix 4: prevent mobile dropdown reopening after variant change ────────
  //
  // selectCombination() is async. After it completes it dispatches
  // "product:rerender" on the form, which the ProductRerender custom element
  // handles by replacing the variant picker DOM (including the <select>).
  // On mobile, inserting a new <select> while a select-related interaction is
  // in progress causes the browser to re-focus the element and pop the
  // native picker open again.
  //
  // Fix: when a variant <select> changes, schedule a blur() after the
  // rerender has had time to settle. We target document.activeElement so the
  // blur hits whichever element (old or new) holds focus at that moment.

  document.addEventListener('change', function (e) {
    if (
      !e.target ||
      e.target.tagName !== 'SELECT' ||
      !e.target.hasAttribute('data-option-position')
    ) return;

    // selectCombination awaits a server fetch before replacing the DOM,
    // typically 200-600 ms. Two scheduled blurs cover both the immediate
    // re-focus and any delayed re-focus from the async rerender.
    [200, 600].forEach(function (delay) {
      setTimeout(function () {
        var active = document.activeElement;
        if (active && active.tagName === 'SELECT') active.blur();
      }, delay);
    });
  }, false); // bubble phase — runs after variant-picker's capture handler

  console.log(LOG, 'custom.js loaded, swap listeners active');
})();
